import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import AquillaButton from 'aquilla/core/molecules/Button';
import { useEffect, useState } from 'react';
import pushRoute from '@utils/pushRoute';
import {
  navigateToCheckoutWithLearnerDetails,
  getCbtUserDetailsSubmittedEventProps
} from '@cont/SeeAll/utils';
import { useRouter } from 'next/router';
import useFetch from '@hooks/useFetch';
import { getAddonsMetaData } from '@cont/Checkout/apiHelper';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import usePricings from '@cont/Checkout/usePricings';
import {
  CHECKOUT_COMBO_GOAL_ADDED,
  CHECKOUT_COMBO_GOAL_REMOVED,
  CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
  CHECKOUT_CBT_USER_DETAILS_SUBMITTED
} from '@cont/Checkout/constants/segment';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { plusProceedPaymentEventAnalytics } from '@cont/Checkout/utils';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import isEmptyObject from '@utils/isEmptyObject';
import {
  getIconicProAutoSelectedBundle,
  getIconicProDefaultBundleOnProceed,
  isIconicProSubscription
} from '@cont/Checkout/bundleAddOnsHelper';
import BundleCbtVenueSelection from '@cont/Checkout/BundleCbtVenueSelection';
import useCbtBundleVenueReservation from '@cont/Checkout/useCbtBundleVenueReservation';
import {
  defaultSelectedPricing,
  discountedPricingIfAvailable
} from '../planSelectionHelper';
import AddonPickerCards from '../BaseComponents/AddonPickerCards';

const AddonsFlowWrapper = styled.div<{ $hasMultipleAddon: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
  margin-bottom: ${({ $hasMultipleAddon }) => $hasMultipleAddon && '150px'};
`;

const Title = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  width: 280px;
`;

const AddonCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_3B};
`;

const BottomCtaWrapper = styled.div<{ $hasMultipleAddon: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  padding-top: ${Spacings.SPACING_4B};
  ${({ $hasMultipleAddon }) => {
    if ($hasMultipleAddon)
      return `
        position: fixed;
        bottom: 0;
        padding-right: ${Spacings.SPACING_8B};
        background-color: var(--color-i-white);
        padding-bottom: ${Spacings.SPACING_4B};
        `;
  }}
`;

const keysToRemoveFromQuery = [
  'combo_subscription_uid',
  'is_combo',
  'parent_subscription_uid'
];

const AddonsFlowPage = ({
  bundleAddOnData,
  purchaseDetails,
  discountedPricings,
  currentEventProps
}) => {
  const [addonBundleData, setAddonBundleData] = useState(bundleAddOnData);

  const { query } = useRouter();

  const {
    parent_subscription_uid,
    combo_subscription_uid,
    is_combo = false,
    goalUID = ''
  } = query;

  const { pricings } = usePricings({ goalUID });
  const { goal = {} } = pricings ?? {};
  const cbtConfig = goal?.cbtConfig;
  const { programmeUid, requiresVenueSelection, reserveVenue } =
    useCbtBundleVenueReservation(cbtConfig);
  const { isLoading: isMeDataLoading, isValidating: isMeDataValidating } =
    useMyInfo();
  const me = useMyInfo(getMe);

  const bundleAddonData =
    addonBundleData?.bundleAddOnsInfo?.bundle_add_ons ?? [];

  const isIconicProPlan = isIconicProSubscription(addonBundleData?.skuDetails);
  const iconicProForcedBundle = isIconicProPlan
    ? getIconicProAutoSelectedBundle(bundleAddonData)
    : null;
  const isIconicProSingleForced = Boolean(iconicProForcedBundle);

  const [selectedAddon, setSelectedAddon] = useState<
    Record<string, any> | undefined
  >(() => {
    if (iconicProForcedBundle) return iconicProForcedBundle;
    if (isIconicProPlan) return undefined;
    return bundleAddonData[0];
  });
  const [venueSelection, setVenueSelection] = useState(null);

  const goalList = bundleAddonData?.map((addOn) => addOn.uid);

  const [addonsMetaData, setAddonsMetaData] =
    useState<Array<Record<string, any> | undefined>>();

  useEffect(() => {
    (async () => {
      getAddonsMetaData(goalList).then((metaData) => {
        if (!metaData?.error) setAddonsMetaData(metaData);
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonBundleData]);

  const { data: bundleAddOnsInfo } = useFetch(
    parent_subscription_uid && combo_subscription_uid
      ? `v2/subscriptions/bundle_add_ons/details/?subscription_uid=${parent_subscription_uid}`
      : null,
    { useCamelCase: false },
    { shouldRetryOnError: false }
  );

  useEffect(() => {
    if (bundleAddOnsInfo) {
      const bundles = bundleAddOnsInfo.bundle_add_ons;
      if (bundles?.length > 0) {
        if (isIconicProSubscription(addonBundleData?.skuDetails)) {
          const forced = getIconicProAutoSelectedBundle(bundles);
          setSelectedAddon(forced || undefined);
        } else {
          setSelectedAddon(bundles[0]);
        }
      }
      setAddonBundleData({ bundleAddOnsInfo });
    }
  }, [addonBundleData?.skuDetails, bundleAddOnsInfo]);

  const hasMultipleAddon =
    !isIconicProSingleForced && bundleAddonData?.length > 1;

  const getActiveAddon = () => {
    if (isIconicProSingleForced) return iconicProForcedBundle;
    if (hasMultipleAddon) return selectedAddon;
    const [firstAddon] = bundleAddonData;
    return firstAddon;
  };
  const activeAddon = getActiveAddon();

  const showCbtVenueSelection =
    activeAddon && requiresVenueSelection(activeAddon);

  const purchaseSubscription = purchaseDetails?.subscription ?? {};
  const { referral = {}, display_duration: primaryDisplayDuration } =
    purchaseSubscription;

  const { referral: { code: referralCode = '' } = {} } = selectedAddon ?? {};

  const isReferralAdded = !!referralCode;

  const eventProps = useCheckoutEventProps(
    goalUID,
    discountedPricingIfAvailable(
      parent_subscription_uid ?? selectedAddon?.subscription?.uid,
      isReferralAdded,
      discountedPricings
    ) || defaultSelectedPricing(selectedAddon?.subscription?.uid, pricings),
    true
  );

  const evtPayload = {
    ...eventProps,
    is_combo: true,
    goal_uid: goalUID,
    goal_name: selectedAddon?.name,
    referral_code: referralCode || 'NA',
    is_referral_successful: true,
    subscription_type: 'Combo',
    subscription_duration: selectedAddon?.subscription?.duration,
    subscription_price: selectedAddon?.originally_for,
    price: selectedAddon?.add_for
  };

  const isMeDataProcessing = isMeDataLoading && isMeDataValidating;

  useEffect(() => {
    if (
      !isMeDataProcessing &&
      parent_subscription_uid &&
      combo_subscription_uid
    )
      plusProceedPaymentEventAnalytics({
        ...evtPayload,
        ...me,
        userId: me?.uid,
        ...goal,
        user_auth: !isEmptyObject(me)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeDataProcessing]);

  const handleAddonPlanSubmit = (plan, learnerDetailsUid = null) => {
    if (learnerDetailsUid) {
      navigateToCheckoutWithLearnerDetails({
        goalSlug: selectedAddon?.slug || query.goalSlug,
        goalUid: selectedAddon?.uid || goalUID,
        subscriptionUid: plan ? plan?.uid : parent_subscription_uid,
        queryParams: {
          learner_details_uid: learnerDetailsUid,
          gUid: goalUID
        }
      });
      return;
    }

    const updatedQueryObject = is_combo
      ? Object.fromEntries(
          Object.entries(query).filter(
            ([key]) => !keysToRemoveFromQuery.includes(key)
          )
        )
      : query;

    pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
      query: {
        ...updatedQueryObject,
        subscriptionUID: plan ? plan?.uid : parent_subscription_uid
      }
    });
  };

  const getAddonSubmitSubscription = () => {
    if (isIconicProPlan) {
      const bundle =
        selectedAddon ||
        iconicProForcedBundle ||
        getIconicProDefaultBundleOnProceed(bundleAddonData);
      return bundle?.subscription;
    }
    if (hasMultipleAddon) {
      return selectedAddon?.subscription;
    }
    return bundleAddonData[0]?.subscription;
  };

  const getPrimaryButtonLabel = () => {
    // Iconic Pro forced combo, or a combo already selected: next tap pays.
    if (isIconicProSingleForced || (isIconicProPlan && selectedAddon)) {
      return 'Continue to pay';
    }
    if (hasMultipleAddon) {
      return 'Add & continue to pay';
    }
    return 'Add optional exam';
  };

  const getAddonsFlowTitle = () => {
    if (isIconicProPlan) return 'Choose any one combo';
    if (hasMultipleAddon) return 'Also prepare for these additional exams?';
    return `Also prepare for the ${bundleAddonData[0]?.name}`;
  };

  return (
    <>
      <AddonsFlowWrapper $hasMultipleAddon={hasMultipleAddon}>
        {bundleAddonData && <Title variant="h2">{getAddonsFlowTitle()}</Title>}
        <AddonCardWrapper>
          <AddonPickerCards
            bundleAddonData={bundleAddonData}
            isIconicProSingleForced={isIconicProSingleForced}
            iconicProForcedBundle={iconicProForcedBundle}
            skuDetailsForForcedBundle={addonBundleData?.skuDetails}
            skuDetailsForList={bundleAddOnData?.skuDetails}
            addonsMetaData={addonsMetaData}
            selectedAddonUid={selectedAddon?.uid}
            hasMultipleAddon={hasMultipleAddon}
            referral={referral}
            primaryDisplayDuration={primaryDisplayDuration}
            onSelectAddon={(addOnData) => {
              setVenueSelection(null);
              setSelectedAddon(addOnData);
            }}
          />
        </AddonCardWrapper>
        {showCbtVenueSelection && (
          <BundleCbtVenueSelection
            programmeUid={programmeUid}
            activationDelayHours={goal?.cbtActivationDelayHours}
            isMobile
            onVenueChange={setVenueSelection}
          />
        )}
      </AddonsFlowWrapper>
      {addonBundleData && (
        <BottomCtaWrapper $hasMultipleAddon={hasMultipleAddon}>
          <AquillaButton
            variant="primary"
            size="large"
            fullWidth
            onClick={async () => {
              let learnerDetailsUid = null;

              const submitBundle = isIconicProPlan
                ? selectedAddon ||
                  iconicProForcedBundle ||
                  getIconicProDefaultBundleOnProceed(bundleAddonData)
                : activeAddon;

              if (isIconicProPlan && submitBundle && !selectedAddon) {
                // First Continue: only select the default combo. Second Continue navigates.
                setVenueSelection(null);
                setSelectedAddon(submitBundle);
                return;
              }

              if (
                submitBundle &&
                requiresVenueSelection(submitBundle) &&
                (showCbtVenueSelection || isIconicProPlan)
              ) {
                if (!venueSelection) return;

                segment.track(
                  CHECKOUT_CBT_USER_DETAILS_SUBMITTED,
                  getCbtUserDetailsSubmittedEventProps({
                    venueSelection,
                    goalSegmentData: {
                      goal_uid: submitBundle?.uid,
                      goal_name: submitBundle?.name,
                      is_subscription_active: eventProps.is_subscription_active
                    },
                    entityProps: evtPayload,
                    programmeComposition: cbtConfig?.programmeComposition
                  }),
                  evtNameCategoryMap.BOTH
                );

                learnerDetailsUid = await reserveVenue(venueSelection);
                if (!learnerDetailsUid) return;
              }

              segment.track(CHECKOUT_COMBO_GOAL_ADDED, evtPayload);
              segment.track(CHECKOUT_PROCEED_TO_PAYMENT_SELECTED, evtPayload);
              handleAddonPlanSubmit(
                submitBundle?.subscription || getAddonSubmitSubscription(),
                learnerDetailsUid
              );
            }}
          >
            {getPrimaryButtonLabel()}
          </AquillaButton>
          {!isIconicProPlan && (
            <AquillaButton
              variant="secondary"
              size="large"
              fullWidth
              onClick={() => {
                segment.track(CHECKOUT_COMBO_GOAL_REMOVED, evtPayload);
                if (currentEventProps)
                  segment.track(
                    CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
                    currentEventProps
                  );
                handleAddonPlanSubmit(bundleAddOnData?.skuDetails);
              }}
            >
              Continue without optional exam
            </AquillaButton>
          )}
        </BottomCtaWrapper>
      )}
    </>
  );
};

export default AddonsFlowPage;
